import React, { useState, useMemo, useEffect } from "react";
import { DEMO_MODE } from "../utils/demoMode";
import { DEMO_CATEGORIES, DEMO_TOP_PRODUCTS, DEMO_RECENT_INVOICES, DEMO_REVENUE_TREND } from "../demo/dashboardDemoData";
// Dashboard — Store Performance & Ledger
import { useQueryClient } from "@tanstack/react-query";
import {
  useDashboardSummary,
  useDashboardRevenueByCategory,
  useDashboardTopProducts,
  useDashboardStockWeight,
} from "../hooks/queries/useDashboard";
import { useInvoices, useInvoiceItems } from "../hooks/queries/useInvoices";
import { useProducts } from "../hooks/queries/useProducts";
import { useCategories } from "../hooks/queries/useCategories";
import { useInventorySummary, useInventoryItems } from "../hooks/queries/useInventory";
import PrintInvoiceModal from "../components/PrintInvoiceModal";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { subscribeDataMutation } from "../utils/appEvents";
import "./common.css";

const CATEGORY_COLORS = [
  "#A07850",
  "#9CA3AF",
  "#3B82F6",
  "#4A4444",
  "#10B981",
  "#D97706",
  "#8B5CF6",
  "#EC4899",
];

const getStockStatusBadge = (pieces) => {
  if (pieces === null || pieces === undefined || isNaN(pieces)) {
    return null;
  }
  const qty = Number(pieces);
  if (qty > 10) {
    return {
      label: "In Stock",
      background: "rgba(39,174,96,.12)",
      color: "#27ae60",
    };
  }
  if (qty >= 4) {
    return {
      label: "Low",
      background: "rgba(243,156,18,.12)",
      color: "#d35400",
    };
  }
  if (qty >= 1) {
    return {
      label: "Critical",
      background: "rgba(192,57,43,.12)",
      color: "#c0392b",
    };
  }
  return {
    label: "Out of Stock",
    background: "rgba(80,0,0,.12)",
    color: "#7b0000",
  };
};

const formatDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPeriodDates = (period) => {
  const now = new Date();
  const todayStr = formatDate(now);
  if (period === "Today") {
    return { from: todayStr, to: todayStr };
  }
  if (period === "This Week") {
    const day = now.getDay();
    const diff = now.getDate() - (day === 0 ? 6 : day - 1);
    const monday = new Date(now.getFullYear(), now.getMonth(), diff);
    return { from: formatDate(monday), to: todayStr };
  }
  if (period === "This Month") {
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: formatDate(firstDay), to: todayStr };
  }
  if (period === "This Year") {
    const firstDay = new Date(now.getFullYear(), 0, 1);
    return { from: formatDate(firstDay), to: todayStr };
  }
  if (period === "All Time") {
    return { from: "", to: "" };
  }
  return { from: "", to: "" };
};

export default function Dashboard({ onNavigate }) {
  const queryClient = useQueryClient();
  const [selectedPeriod, setSelectedPeriod] = useState("This Month");
  const initialDates = useMemo(() => getPeriodDates("This Month"), []);
  const [fromDate, setFromDate] = useState(initialDates.from);
  const [toDate, setToDate] = useState(initialDates.to);
  const [appliedDates, setAppliedDates] = useState(initialDates);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [printInvoice, setPrintInvoice] = useState(null);

  const queryParams = useMemo(() => {
    const p = {};
    if (appliedDates.from) p.from = appliedDates.from;
    if (appliedDates.to) p.to = appliedDates.to;
    return p;
  }, [appliedDates]);

  // Hook queries
  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useDashboardSummary(queryParams);

  const {
    data: revenueByCategoryData,
    isLoading: revCatLoading,
    isError: revCatError,
    refetch: refetchRevCat,
  } = useDashboardRevenueByCategory(queryParams);

  const {
    data: topProductsData,
    isLoading: topProductsLoading,
    isError: topProductsError,
    refetch: refetchTopProducts,
  } = useDashboardTopProducts(queryParams);

  const {
    isLoading: stockWeightLoading,
    isError: stockWeightError,
    refetch: refetchStockWeight,
  } = useDashboardStockWeight();

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    isError: invoicesError,
    refetch: refetchInvoices,
  } = useInvoices();

  const {
    data: invoiceItemsData,
    refetch: refetchInvoiceItems,
  } = useInvoiceItems();

  const { data: productsData, refetch: refetchProducts } = useProducts();
  const { data: categoriesData, refetch: refetchCategories } = useCategories();
  const { data: inventoryData, refetch: refetchInventory } = useInventorySummary();
  const { data: inventoryItemsData, refetch: refetchInventoryItems } = useInventoryItems();

  // Cross-tab and live UI mutations sync
  useEffect(() => {
    const unsubscribe = subscribeDataMutation(() => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-revenue-category"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-top-products"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stock-weight"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoice-items"] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["inventorySummary"] });
      queryClient.invalidateQueries({ queryKey: ["inventoryItems"] });
    });
    return unsubscribe;
  }, [queryClient]);

  const navigateTo = (pageName) => {
    const target =
      pageName === "Inventory & Stocks"
        ? "Stocks"
        : pageName === "Movement History"
          ? "Inventory Transaction"
          : pageName;
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: target }));
    if (typeof onNavigate === "function") {
      onNavigate(target);
    }
  };

  const handleShortPath = (pageName, modalAction) => {
    sessionStorage.setItem("reva_auto_modal", modalAction);
    navigateTo(pageName);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("reva:open_modal", { detail: modalAction }));
    }, 60);
  };

  const handlePeriodChange = (p) => {
    setSelectedPeriod(p);
    const dates = getPeriodDates(p);
    setFromDate(dates.from);
    setToDate(dates.to);
    setAppliedDates(dates);
  };

  const handleApplyDates = () => {
    setSelectedPeriod("Custom");
    setAppliedDates({ from: fromDate, to: toDate });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.allSettled([
      refetchSummary(),
      refetchRevCat(),
      refetchTopProducts(),
      refetchStockWeight(),
      refetchInvoices(),
      refetchInvoiceItems(),
      refetchProducts(),
      refetchCategories(),
      refetchInventory(),
      refetchInventoryItems(),
    ]);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  const periods = ["Today", "This Week", "This Month", "This Year", "All Time"];

  // Processed Category Revenue List directly from Finalised Invoices
  const rawCategories = useMemo(() => {
    // If invoice data is available, compute directly from real finalised invoices
    if (Array.isArray(invoicesData) && invoicesData.length > 0 && Array.isArray(productsData) && Array.isArray(categoriesData)) {
      // Map: productID -> categoryID
      const prodCategoryMap = new Map();
      productsData.forEach(p => {
        if (p.productID != null && p.categoryID != null) {
          prodCategoryMap.set(Number(p.productID), Number(p.categoryID));
        }
      });

      // Map: categoryID -> categoryName
      const catNameMap = new Map();
      categoriesData.forEach(c => {
        if (c.categoryID != null) {
          catNameMap.set(Number(c.categoryID), c.categoryName || `Category ${c.categoryID}`);
        }
      });

      // Map: invoiceID -> array of items
      const invoiceItemsMap = new Map();
      if (Array.isArray(invoiceItemsData)) {
        invoiceItemsData.forEach(item => {
          const invId = Number(item.invoiceID);
          if (!invId) return;
          if (!invoiceItemsMap.has(invId)) {
            invoiceItemsMap.set(invId, []);
          }
          invoiceItemsMap.get(invId).push(item);
        });
      }

      // Filter finalised invoices within the selected date range
      const filteredInvoices = invoicesData.filter(inv => {
        if (inv.invoiceStatus !== "Finalised") return false;
        const invDate = inv.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : "";
        if (appliedDates?.from && invDate && invDate < appliedDates.from) return false;
        if (appliedDates?.to && invDate && invDate > appliedDates.to) return false;
        return true;
      });

      if (filteredInvoices.length > 0) {
        // Accumulate revenue per category from invoice gross amounts
        const categoryTotals = new Map();

        filteredInvoices.forEach(inv => {
          const invGross = Number(inv.grossAmount || inv.totalAmount || 0);
          const items = invoiceItemsMap.get(Number(inv.invoiceID)) || [];

          if (items.length === 0) {
            const catName = "Other";
            categoryTotals.set(catName, (categoryTotals.get(catName) || 0) + invGross);
            return;
          }

          // Total base for items
          const totalLinesBase = items.reduce((sum, item) => sum + (Number(item.totalPrice || item.lineAmount) || 1), 0);

          items.forEach(item => {
            const catId = prodCategoryMap.get(Number(item.productID));
            const catName = (catId && catNameMap.get(catId)) || "General";

            const itemShare = totalLinesBase > 0
              ? (Number(item.totalPrice || item.lineAmount || 1) / totalLinesBase) * invGross
              : invGross / items.length;

            categoryTotals.set(catName, (categoryTotals.get(catName) || 0) + itemShare);
          });
        });

        const grandTotal = Array.from(categoryTotals.values()).reduce((sum, v) => sum + v, 0);

        return Array.from(categoryTotals.entries())
          .map(([name, rev], i) => {
            const revenue = Math.round(rev * 100) / 100;
            const pct = grandTotal > 0 ? Math.round((revenue / grandTotal) * 1000) / 10 : 0;
            return {
              name,
              revenue,
              pct,
              color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
            };
          })
          .sort((a, b) => b.revenue - a.revenue);
      }
      return [];
    }

    // Fallback to backend summary data if invoices not ready
    if (!Array.isArray(revenueByCategoryData)) return [];
    return revenueByCategoryData.map((c, i) => ({
      name: c.categoryName || c.name || `Category ${i + 1}`,
      revenue: Number(c.revenue || c.totalRevenue || 0),
      pct: Number(c.pct || c.percentage || 0),
      color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
    }));
  }, [invoicesData, invoiceItemsData, productsData, categoriesData, appliedDates, revenueByCategoryData]);

  const categories = DEMO_MODE ? DEMO_CATEGORIES : rawCategories;


  // Inventory summary processing for real live stock health
  const inventoryList = useMemo(() => {
    return Array.isArray(inventoryData) ? inventoryData : inventoryData?.data ?? [];
  }, [inventoryData]);


  // Sum quantity from inventorySummaries across all products whose categoryID matches
  const categoryPiecesMap = useMemo(() => {
    const byCatId = new Map();
    const byCatName = new Map();

    if (Array.isArray(productsData) && productsData.length > 0 && Array.isArray(inventoryList)) {
      const prodToCat = new Map();
      productsData.forEach((p) => {
        if (p.productID != null && p.categoryID != null) {
          prodToCat.set(Number(p.productID), Number(p.categoryID));
        }
      });

      inventoryList.forEach((s) => {
        const pId = Number(s.productID);
        const qty = Number(s.quantity || 0);
        const catId = prodToCat.get(pId);
        if (catId != null) {
          byCatId.set(catId, (byCatId.get(catId) || 0) + qty);
        }
      });

      if (Array.isArray(categoriesData)) {
        categoriesData.forEach((c) => {
          const cId = Number(c.categoryID);
          const cName = (c.categoryName || "").trim().toLowerCase();
          if (cId != null && cName) {
            byCatName.set(cName, byCatId.get(cId) || 0);
          }
        });
      }
    }

    return { byCatId, byCatName };
  }, [productsData, inventoryList, categoriesData]);

  // Combined Category Portfolios with Revenue & Stock Pieces
  const combinedCategoryData = useMemo(() => {
    const map = new Map();

    categories.forEach((c) => {
      const key = (c.name || "").trim().toLowerCase();
      let pieces = undefined;
      if (categoryPiecesMap.byCatName.has(key)) {
        pieces = categoryPiecesMap.byCatName.get(key);
      } else if (c.pieces !== undefined) {
        pieces = c.pieces;
      }

      map.set(key, {
        name: c.name,
        color: c.color,
        revenue: c.revenue || 0,
        revenuePct: c.pct || 0,
        pieces: pieces,
      });
    });

    if (Array.isArray(categoriesData)) {
      categoriesData.forEach((cd, i) => {
        const key = (cd.categoryName || "").trim().toLowerCase();
        if (key && !map.has(key)) {
          const pieces = categoryPiecesMap.byCatId.get(Number(cd.categoryID)) ?? categoryPiecesMap.byCatName.get(key);
          if (pieces !== undefined && pieces > 0) {
            map.set(key, {
              name: cd.categoryName,
              color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
              revenue: 0,
              revenuePct: 0,
              pieces: pieces,
            });
          }
        }
      });
    }

    return Array.from(map.values());
  }, [categories, categoryPiecesMap, categoriesData]);


  // Real sparkline points from finalised invoices in period
  const rawRevenueTrendPoints = useMemo(() => {
    if (!Array.isArray(invoicesData) || invoicesData.length === 0) return [];
    const finalised = invoicesData.filter(inv => {
      if (inv.invoiceStatus !== "Finalised") return false;
      const invDate = inv.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : "";
      if (appliedDates?.from && invDate && invDate < appliedDates.from) return false;
      if (appliedDates?.to && invDate && invDate > appliedDates.to) return false;
      return true;
    });

    if (finalised.length === 0) return [];

    const dailyMap = new Map();
    finalised.forEach(inv => {
      const d = inv.invoiceDate ? String(inv.invoiceDate).slice(0, 10) : "today";
      const amt = Number(inv.totalAmount || inv.grossAmount || 0);
      dailyMap.set(d, (dailyMap.get(d) || 0) + amt);
    });

    const sortedDates = Array.from(dailyMap.keys()).sort();
    return sortedDates.map(d => ({ date: d, amount: dailyMap.get(d) }));
  }, [invoicesData, appliedDates]);

  // Recent Invoices (Latest 5)
  const revenueTrendPoints = DEMO_MODE ? DEMO_REVENUE_TREND : rawRevenueTrendPoints;
  const rawRecentInvoices = useMemo(() => {
    if (!Array.isArray(invoicesData)) return [];
    return [...invoicesData]
      .sort((a, b) => (b.invoiceID || 0) - (a.invoiceID || 0))
      .slice(0, 5);
  }, [invoicesData]);

  // Top Products List
  const recentInvoices = DEMO_MODE ? DEMO_RECENT_INVOICES : rawRecentInvoices;
  const rawTopProducts = useMemo(() => {
    if (!Array.isArray(topProductsData)) return [];
    return topProductsData;
  }, [topProductsData]);

  // Processed Donut SVG segments with pre-computed offsets
  const topProducts = DEMO_MODE ? DEMO_TOP_PRODUCTS : rawTopProducts;
  const donutSlices = useMemo(() => {
    if (!categories.length) return [];
    const r = 82;
    const circ = 2 * Math.PI * r;
    let offset = 0;
    return categories.map((cat) => {
      const pct = Math.max(0, Math.min(100, cat.pct || 0));
      const len = (pct / 100) * circ;
      const currentOffset = offset;
      offset += len;
      return {
        ...cat,
        len,
        circ,
        offset: currentOffset,
      };
    });
  }, [categories]);

  // Standardized Core Category Split (Gold, Silver, Diamond, Platinum)
  const coreCategorySplits = useMemo(() => {
    const pillars = [
      { key: "gold", name: "Gold", color: "#A07850" },
      { key: "silver", name: "Silver", color: "#9CA3AF" },
      { key: "diamond", name: "Diamond", color: "#3B82F6" },
      { key: "platinum", name: "Platinum", color: "#4A4444" },
    ];

    const sourceCategories = Array.isArray(categories) ? categories : [];
    const overallTotal = sourceCategories.reduce((sum, c) => sum + (Number(c.revenue) || 0), 0);
    return pillars.map((pillar) => {
      const matches = sourceCategories.filter((c) =>
        (c.name || "").toLowerCase().includes(pillar.key)
      );
      const totalRev = matches.reduce((sum, c) => sum + (Number(c.revenue) || 0), 0);
      const sharePct = overallTotal > 0 ? Math.round((totalRev / overallTotal) * 100) : 0;
      return {
        ...pillar,
        revenue: totalRev,
        hasData: totalRev > 0,
        sharePct,
      };
    });
  }, [categories]);

  // Monthly Revenue Trend (Dynamic Rolling Last 6 Months)
  const monthlySalesData = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const monthIndex = d.getMonth();
      const monthShort = d.toLocaleString("en-US", { month: "short" });
      const yearShort = String(year).slice(-2);
      const label = `${monthShort} ${yearShort}`;
      const yyyymm = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
      months.push({ yyyymm, month: label, revenue: 0 });
    }

    if (Array.isArray(invoicesData)) {
      invoicesData.forEach((inv) => {
        if (inv.invoiceStatus !== "Finalised") return;
        const invDate = inv.invoiceDate ? String(inv.invoiceDate).slice(0, 7) : "";
        const m = months.find((item) => item.yyyymm === invDate);
        if (m) {
          m.revenue += Number(inv.grossAmount || inv.totalAmount || 0);
        }
      });
    }

    const totalLive = months.reduce((sum, m) => sum + m.revenue, 0);

    if (DEMO_MODE || totalLive === 0) {
      const demoAmounts = [38000, 42000, 29000, 51000, 47000, 61000];
      months.forEach((m, idx) => {
        m.revenue = demoAmounts[idx % demoAmounts.length];
      });
    }

    let maxRev = 0;
    let best = months[months.length - 1];
    months.forEach((m) => {
      if (m.revenue >= maxRev) {
        maxRev = m.revenue;
        best = m;
      }
    });

    const formattedMonths = months.map((m) => ({
      ...m,
      isBest: best.revenue > 0 ? m === best : false,
      label: m.revenue >= 1000 ? `₹${Math.round(m.revenue / 1000)}k` : `₹${Math.round(m.revenue)}`,
    }));

    const bestFormatted = formattedMonths.find((m) => m.isBest) || formattedMonths[formattedMonths.length - 1];

    return {
      months: formattedMonths,
      bestMonth: bestFormatted,
      maxRevenue: Math.max(...months.map((m) => m.revenue), 1000),
    };
  }, [invoicesData]);

  return (
    <div className="rv-dashboard-root">
      <style>{`
        .rv-dashboard-root {
          padding: 24px 32px 48px;
          background: #FAF7F2;
          min-height: 100%;
          color: #2C2C2C;
          font-family: var(--b-font, 'Montserrat', sans-serif);
          box-sizing: border-box;
        }

        .rv-serif-heading {
          font-family: 'Cormorant Garamond', 'Cinzel Decorative', Georgia, serif;
          font-weight: 600;
          letter-spacing: -0.01em;
          color: #2C2C2C;
        }

        .rv-badge-label {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #A07850;
        }

        /* ── Header Area ── */
        .rv-db-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 22px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .rv-db-title-area {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .rv-db-main-title {
          font-size: 28px;
          margin: 0;
          line-height: 1.2;
        }

        .rv-db-header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .rv-period-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 6px 14px;
          border-radius: 20px;
          background: #FFFFFF;
          border: 1px solid rgba(160, 120, 80, 0.2);
          font-size: 12px;
          font-weight: 600;
          color: #2C2C2C;
          box-shadow: 0 1px 4px rgba(0,0,0,0.03);
        }

        .rv-pulse-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #27AE60;
          box-shadow: 0 0 0 3px rgba(39, 174, 96, 0.2);
        }

        /* ── Filter Bar ── */
        .rv-filter-bar {
          background: #FFFFFF;
          border: 1px solid rgba(160, 120, 80, 0.18);
          border-radius: 12px;
          padding: 10px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          box-shadow: 0 2px 8px rgba(51, 47, 47, 0.03);
          flex-wrap: wrap;
          gap: 16px;
        }

        .rv-filter-left {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-wrap: wrap;
        }

        .rv-filter-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #8A7A6B;
          text-transform: uppercase;
        }

        .rv-filter-pills {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .rv-filter-pill-btn {
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 500;
          border-radius: 20px;
          cursor: pointer;
          border: 1px solid transparent;
          transition: all 0.18s ease;
          background: transparent;
          color: #6E5F52;
        }

        .rv-filter-pill-btn:hover {
          color: #A07850;
          background: rgba(160, 120, 80, 0.06);
        }

        .rv-filter-pill-btn.active {
          background: #A07850;
          color: #FFFFFF;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(160, 120, 80, 0.28);
        }

        .rv-filter-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .rv-date-input-wrap {
          display: flex;
          align-items: center;
          background: #FAF7F2;
          border: 1px solid rgba(160, 120, 80, 0.22);
          border-radius: 8px;
          padding: 5px 8px;
          gap: 6px;
          box-sizing: border-box;
          min-width: 0;
          overflow: hidden;
        }

        .rv-date-input-label {
          font-size: 10px;
          font-weight: 700;
          color: #8A7A6B;
          letter-spacing: 0.06em;
          flex-shrink: 0;
          user-select: none;
        }

        .rv-date-input {
          font-size: 11.5px;
          color: #2C2C2C;
          border: none;
          background: transparent;
          outline: none;
          width: 100%;
          min-width: 0;
          flex: 1;
          font-family: inherit;
          padding: 0;
          margin: 0;
          box-sizing: border-box;
        }

        .rv-date-input::-webkit-calendar-picker-indicator {
          cursor: pointer;
          opacity: 0.65;
          margin: 0;
          padding: 0;
          width: 13px;
          height: 13px;
          flex-shrink: 0;
          transition: opacity 0.15s ease;
        }

        .rv-date-input::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
        }

        .rv-apply-btn {
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 600;
          border-radius: 8px;
          background: #FAF7F2;
          border: 1px solid rgba(160, 120, 80, 0.28);
          color: #A07850;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .rv-apply-btn:hover {
          background: #A07850;
          color: #FFFFFF;
          border-color: #A07850;
        }

        /* ── KPI Grid (Compact Sales Card + Expanded Category Split) ── */
        .rv-kpi-grid {
          display: grid;
          grid-template-columns: 280px 1fr;
          gap: 18px;
          margin-bottom: 24px;
          align-items: stretch;
        }

        @media (max-width: 900px) {
          .rv-kpi-grid {
            grid-template-columns: 1fr !important;
          }
        }

        .rv-kpi-card {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 18px 20px;
          box-shadow: 0 2px 10px rgba(51, 47, 47, 0.04);
          border: 1px solid rgba(160, 120, 80, 0.16);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          position: relative;
        }

        .rv-kpi-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .rv-kpi-value {
          font-size: 26px;
          line-height: 1.1;
          margin-bottom: 4px;
        }

        .rv-kpi-sub {
          font-size: 11.5px;
          color: #8A7A6B;
          margin-bottom: 14px;
        }

        .rv-kpi-badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 8px;
          border-radius: 12px;
          font-size: 10.5px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }

        .rv-kpi-badge--green {
          background: rgba(39, 174, 96, 0.12);
          color: #27AE60;
        }

        .rv-kpi-badge--gold {
          background: rgba(160, 120, 80, 0.12);
          color: #A07850;
        }

        .rv-kpi-badge--amber {
          background: rgba(217, 119, 6, 0.12);
          color: #D97706;
        }

        /* Category Split Subparts */
        .rv-kpi-split-subparts {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          margin-top: 6px;
          margin-bottom: 10px;
        }

        @media (max-width: 1200px) {
          .rv-kpi-split-subparts {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .rv-kpi-subpart-item {
          background: #FAF7F2;
          border: 1px solid rgba(160, 120, 80, 0.14);
          border-radius: 9px;
          padding: 10px 12px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: all 0.15s ease;
        }

        .rv-kpi-subpart-item--active {
          background: #FFFFFF;
          border-color: rgba(160, 120, 80, 0.35);
          box-shadow: 0 1px 4px rgba(160, 120, 80, 0.08);
        }

        .rv-kpi-subpart-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
        }

        .rv-kpi-subpart-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11.5px;
          font-weight: 700;
          color: #2C2C2C;
          letter-spacing: 0.02em;
        }

        .rv-kpi-subpart-value {
          font-size: 15px;
          font-weight: 700;
          color: #2C2C2C;
          line-height: 1.2;
          margin-bottom: 3px;
        }

        .rv-kpi-subpart-sub {
          font-size: 10px;
          color: #8A7A6B;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .rv-kpi-card-split-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          margin-bottom: 6px;
        }

        .rv-kpi-card-split-row:last-child {
          margin-bottom: 12px;
        }

        .rv-kpi-dot-name {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #4A4444;
          font-size: 12px;
        }

        .rv-kpi-bottom-meta {
          border-top: 1px solid rgba(160, 120, 80, 0.1);
          padding-top: 10px;
          font-size: 11px;
          color: #8A7A6B;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        /* ── Middle Split Grid (Expanded Monthly Revenue Trend & Category Portfolios) ── */
        .rv-middle-split-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.22fr) minmax(0, 1fr);
          gap: 20px;
          margin-bottom: 24px;
          align-items: stretch;
        }

        @media (max-width: 1050px) {
          .rv-middle-split-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        .rv-chart-card,
        .rv-portfolios-card {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 22px;
          border: 1px solid rgba(160, 120, 80, 0.16);
          box-shadow: 0 2px 10px rgba(51, 47, 47, 0.04);
          margin-bottom: 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .rv-portfolios-grid {
          display: grid;
          grid-template-columns: 185px 1fr;
          gap: 16px;
          align-items: center;
        }

        @media (max-width: 720px) {
          .rv-portfolios-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }

        .rv-card-title-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 14px;
        }

        .rv-card-heading {
          font-size: 19px;
          margin: 0 0 4px 0;
        }

        .rv-card-subheading {
          font-size: 12px;
          color: #8A7A6B;
          margin: 0;
        }

        .rv-highlight-pill {
          background: #FAF5EE;
          border: 1px solid rgba(160, 120, 80, 0.22);
          border-radius: 16px;
          padding: 4px 12px;
          font-size: 11.5px;
          font-weight: 600;
          color: #A07850;
          white-space: nowrap;
        }

        .rv-chart-meta-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-top: 1px solid rgba(160, 120, 80, 0.1);
          padding-top: 12px;
          margin-top: 12px;
          font-size: 11.5px;
          flex-wrap: wrap;
          gap: 8px;
        }

        /* ── Category Portfolios Subsections ── */
        .rv-portfolio-sub {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .rv-sub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .rv-sub-title {
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #8A7A6B;
        }

        .rv-sub-right {
          font-size: 11px;
          font-weight: 600;
          color: #A07850;
        }

        .rv-donut-container {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 28px;
          margin-bottom: 14px;
          flex-wrap: wrap;
        }

        .rv-donut-legend {
          display: flex;
          flex-direction: column;
          gap: 8px;
          flex: 1;
          min-width: 200px;
        }

        .rv-legend-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: #FAF7F2;
          border-radius: 8px;
          border: 1px solid rgba(160, 120, 80, 0.12);
          font-size: 12px;
          color: #2C2C2C;
          transition: background 0.15s ease;
        }

        .rv-legend-item:hover {
          background: #F5EFE6;
        }

        .rv-legend-item-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .rv-legend-item-name {
          font-weight: 600;
          color: #2C2C2C;
        }

        .rv-legend-item-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .rv-legend-item-rev {
          font-weight: 600;
          color: #6E5F52;
          font-size: 11.5px;
        }

        .rv-legend-item-pct {
          background: rgba(160, 120, 80, 0.14);
          color: #8C643E;
          font-size: 11px;
          font-weight: 700;
          padding: 2px 7px;
          border-radius: 10px;
        }

        .rv-color-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        /* ── Two Column Bottom Cards (Best Sellers & Quick Actions) ── */
        .rv-two-col-grid {
          display: grid;
          grid-template-columns: 1.25fr 1fr;
          gap: 18px;
          margin-bottom: 24px;
        }

        @media (max-width: 992px) {
          .rv-two-col-grid {
            grid-template-columns: 1fr;
          }
        }

        .rv-col-card {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(160, 120, 80, 0.16);
          box-shadow: 0 2px 10px rgba(51, 47, 47, 0.04);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .rv-top-products-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 14px;
        }

        .rv-product-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          padding: 4px 0;
          border-bottom: 1px dashed rgba(160, 120, 80, 0.12);
        }

        .rv-product-row:last-child {
          border-bottom: none;
        }

        .rv-product-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .rv-product-rank {
          font-weight: 700;
          color: #A07850;
          font-size: 11px;
          width: 14px;
        }

        .rv-product-name {
          font-weight: 600;
          color: #2C2C2C;
        }

        .rv-product-sold {
          font-size: 11px;
          color: #8A7A6B;
        }

        .rv-growth-pill {
          font-size: 11px;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 10px;
        }

        .rv-growth-pill--up {
          background: rgba(39, 174, 96, 0.12);
          color: #27AE60;
        }

        .rv-growth-pill--neutral {
          background: rgba(160, 120, 80, 0.1);
          color: #8A7A6B;
        }

        .rv-card-action-link {
          font-size: 11.5px;
          font-weight: 600;
          color: #A07850;
          cursor: pointer;
          text-decoration: none;
          transition: color 0.18s ease;
        }

        .rv-card-action-link:hover {
          color: #7B5B3B;
          text-decoration: underline;
        }

        /* ── Vault Stats ── */
        .rv-vault-stats-box {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          background: #FAF7F2;
          border-radius: 8px;
          padding: 10px;
          text-align: center;
          margin-bottom: 16px;
          border: 1px solid rgba(160, 120, 80, 0.12);
        }

        .rv-vault-stat-col {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .rv-vault-stat-label {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: #8A7A6B;
        }

        .rv-vault-stat-value {
          font-size: 15px;
          font-weight: 700;
        }

        .rv-progress-item {
          margin-bottom: 12px;
        }

        .rv-progress-labels {
          display: flex;
          justify-content: space-between;
          font-size: 11.5px;
          color: #4A4444;
          margin-bottom: 4px;
        }

        .rv-progress-track-bg {
          height: 6px;
          background: #EFE9E0;
          border-radius: 3px;
          overflow: hidden;
        }

        .rv-progress-bar-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.4s ease;
        }

        /* ── Low Stock Alert ── */
        .rv-alert-count-badge {
          background: rgba(217, 119, 6, 0.14);
          color: #D97706;
          font-size: 11px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 12px;
        }

        .rv-low-stock-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 14px;
        }

        .rv-low-stock-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 8px;
          border-bottom: 1px dashed rgba(160, 120, 80, 0.12);
        }

        .rv-low-stock-row:last-child {
          border-bottom: none;
        }

        .rv-amber-pill {
          background: #FFFBEB;
          border: 1px solid rgba(217, 119, 6, 0.28);
          color: #D97706;
          font-size: 10.5px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 10px;
          white-space: nowrap;
        }

        .rv-red-pill {
          background: #FEF2F2;
          border: 1px solid rgba(220, 38, 38, 0.28);
          color: #DC2626;
          font-size: 10.5px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 10px;
          white-space: nowrap;
        }

        .rv-green-pill {
          background: #F0FDF4;
          border: 1px solid rgba(34, 197, 94, 0.28);
          color: #16A34A;
          font-size: 10.5px;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 10px;
          white-space: nowrap;
        }

        /* ── Quick Actions Grid (Card 3) ── */
        .rv-quick-actions-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          margin-bottom: 14px;
        }

        .rv-quick-action-card {
          background: #FAF7F2;
          border: 1px solid rgba(160, 120, 80, 0.2);
          border-radius: 10px;
          padding: 14px 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
          font-family: inherit;
        }

        .rv-quick-action-card:hover {
          background: #FFFFFF;
          border-color: #A07850;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(160, 120, 80, 0.12);
        }

        .rv-quick-action-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: rgba(160, 120, 80, 0.1);
          color: #A07850;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .rv-quick-action-card:hover .rv-quick-action-icon {
          background: #A07850;
          color: #FFFFFF;
        }

        .rv-quick-action-name {
          font-size: 12px;
          font-weight: 600;
          color: #2C2C2C;
          line-height: 1.2;
        }

        /* ── Table Card ── */
        .rv-table-card {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 22px;
          border: 1px solid rgba(160, 120, 80, 0.16);
          box-shadow: 0 2px 10px rgba(51, 47, 47, 0.04);
          margin-bottom: 24px;
        }

        .rv-table-wrap {
          overflow-x: auto;
        }

        .rv-custom-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12.5px;
        }

        .rv-custom-table th {
          text-align: left;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: #8A7A6B;
          padding: 10px 14px;
          border-bottom: 1px solid rgba(160, 120, 80, 0.18);
          background: #FAF7F2;
        }

        .rv-custom-table td {
          padding: 12px 14px;
          border-bottom: 1px solid rgba(160, 120, 80, 0.08);
          vertical-align: middle;
        }

        .rv-invoice-no {
          font-weight: 600;
          color: #A07850;
        }

        .rv-customer-name {
          font-weight: 600;
          color: #2C2C2C;
        }

        .rv-customer-phone {
          font-size: 11px;
          color: #8A7A6B;
        }

        .rv-items-desc {
          max-width: 220px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #5A524C;
        }

        .rv-amount-val {
          font-weight: 700;
          color: #2C2C2C;
        }

        .rv-pill-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 3px 9px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
        }

        .rv-pill-badge--finalised {
          background: rgba(39, 174, 96, 0.12);
          color: #27AE60;
        }

        .rv-pill-badge--draft {
          background: rgba(217, 119, 6, 0.12);
          color: #D97706;
        }

        .rv-table-action-btn {
          padding: 4px 12px;
          font-size: 11.5px;
          font-weight: 600;
          border-radius: 6px;
          border: 1px solid rgba(160, 120, 80, 0.28);
          background: #FAF7F2;
          color: #A07850;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .rv-table-action-btn:hover {
          background: #A07850;
          color: #FFFFFF;
        }

        /* ── Executive Strip ── */
        .rv-executive-strip {
          background: #FFFFFF;
          border-radius: 12px;
          padding: 16px 22px;
          border: 1px solid rgba(160, 120, 80, 0.18);
          box-shadow: 0 2px 8px rgba(51, 47, 47, 0.03);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
        }

        .rv-strip-left {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .rv-strip-buttons {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .rv-strip-btn {
          padding: 8px 16px;
          font-size: 12px;
          font-weight: 600;
          border-radius: 8px;
          background: #A07850;
          color: #FFFFFF;
          border: 1px solid #8C643E;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.18s ease;
        }

        .rv-strip-btn:hover {
          background: #8C643E;
        }

        .rv-filter-date-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .rv-date-sep {
          color: #8A7A6B;
          font-size: 12px;
        }

        .rv-table-desktop {
          display: block;
        }

        .rv-invoices-mobile-list {
          display: none;
        }

        /* ── Footer ── */
        .rv-dashboard-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #8A7A6B;
          border-top: 1px solid rgba(160, 120, 80, 0.12);
          padding-top: 16px;
          flex-wrap: wrap;
          gap: 10px;
        }

        /* ════════════════════════════════════════════════════════════
           RESPONSIVE MOBILE STYLES (<= 640px)
        ════════════════════════════════════════════════════════════ */
        @media (max-width: 640px) {
          .rv-dashboard-root {
            padding: 16px 12px 36px;
          }

          /* Header */
          .rv-db-header {
            margin-bottom: 14px;
            gap: 10px;
          }

          .rv-db-main-title {
            font-size: 22px;
          }

          .rv-db-header-actions {
            width: 100%;
            display: flex;
            gap: 8px;
          }

          .rv-period-status-pill {
            flex: 1;
            justify-content: center;
            padding: 7px 10px;
            font-size: 11px;
          }

          .rv-refresh-btn {
            flex: 1;
            justify-content: center;
            padding: 7px 10px;
            font-size: 11px;
          }

          /* Filter Bar */
          .rv-filter-bar {
            padding: 12px;
            margin-bottom: 16px;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .rv-filter-left {
            width: 100%;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .rv-filter-pills {
            width: 100%;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 4px;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }

          .rv-filter-pills::-webkit-scrollbar {
            display: none;
          }

          .rv-filter-pill-btn {
            flex-shrink: 0;
            white-space: nowrap;
            padding: 5px 12px;
            font-size: 11.5px;
          }

          .rv-filter-right {
            width: 100%;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
          }

          .rv-filter-date-row {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .rv-date-input-wrap {
            flex: 1;
            min-width: 0;
            padding: 5px 8px;
            box-sizing: border-box;
            overflow: hidden;
          }

          .rv-date-input {
            width: 100%;
            min-width: 0;
            flex: 1;
            font-size: 11px;
          }

          .rv-apply-filter-btn {
            width: 100%;
            text-align: center;
            padding: 8px 12px;
          }

          /* KPI Grid on mobile (Single Column) ── */
          .rv-kpi-grid {
            grid-template-columns: 1fr !important;
            gap: 12px;
            margin-bottom: 16px;
          }

          .rv-kpi-card {
            padding: 12px;
          }

          .rv-kpi-top {
            margin-bottom: 8px;
          }

          .rv-badge-label {
            font-size: 9px;
          }

          .rv-kpi-badge {
            font-size: 9.5px;
            padding: 2px 6px;
          }

          .rv-kpi-value {
            font-size: 18px;
            margin-bottom: 2px;
          }

          .rv-kpi-sub {
            font-size: 10px;
            margin-bottom: 8px;
            line-height: 1.25;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .rv-kpi-cat-name-text {
            max-width: 60px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .rv-kpi-card-split-row {
            font-size: 10.5px;
            margin-bottom: 4px;
          }

          .rv-kpi-bottom-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 3px;
            font-size: 9.5px;
            padding-top: 6px;
          }

          /* Middle Split Cards */
          .rv-chart-card,
          .rv-portfolios-card {
            padding: 14px;
            margin-bottom: 14px;
          }

          .rv-card-title-row {
            flex-direction: column;
            gap: 8px;
          }

          .rv-card-heading {
            font-size: 17px;
          }

          .rv-donut-container {
            flex-direction: column;
            align-items: center;
            gap: 16px;
          }

          .rv-donut-legend {
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          /* Two Column Section */
          .rv-two-col-grid {
            gap: 14px;
            margin-bottom: 16px;
          }

          .rv-col-card {
            padding: 14px;
          }

          .rv-vault-stats-box {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            padding: 8px;
          }

          /* Invoices Table Card & Mobile Cards */
          .rv-table-card {
            padding: 14px;
            margin-bottom: 16px;
          }

          .rv-table-desktop {
            display: none;
          }

          .rv-invoices-mobile-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
          }

          .rv-inv-mob-card {
            background: #FAF7F2;
            border: 1px solid rgba(160, 120, 80, 0.18);
            border-radius: 10px;
            padding: 12px 14px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .rv-inv-mob-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .rv-inv-mob-no {
            font-weight: 700;
            color: #A07850;
            font-size: 13px;
          }

          .rv-inv-mob-body {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .rv-inv-mob-customer {
            font-weight: 600;
            font-size: 13px;
            color: #2C2C2C;
          }

          .rv-inv-mob-phone {
            font-size: 11px;
            color: #8A7A6B;
          }

          .rv-inv-mob-desc {
            font-size: 11px;
            color: #6E5F52;
            margin-top: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .rv-inv-mob-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-top: 1px dashed rgba(160, 120, 80, 0.16);
            padding-top: 8px;
            margin-top: 2px;
          }

          .rv-inv-mob-amount {
            font-size: 15px;
            font-weight: 700;
            color: #2C2C2C;
          }

          /* Executive Short-Paths */
          .rv-executive-strip {
            padding: 14px;
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
            margin-bottom: 16px;
          }

          .rv-strip-buttons {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            width: 100%;
          }

          .rv-strip-btn {
            width: 100%;
            justify-content: center;
            padding: 10px 8px;
            font-size: 12px;
            box-sizing: border-box;
          }

          /* Footer */
          .rv-dashboard-footer {
            flex-direction: column;
            text-align: center;
            gap: 6px;
          }
        }

        @media (max-width: 380px) {
          .rv-kpi-grid {
            grid-template-columns: 1fr !important;
          }

          .rv-donut-legend {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* ── 1. Page Heading Area ── */}
      <div className="rv-db-header">
        <div className="rv-db-title-area">
          <span className="rv-badge-label">
            STORE OVERVIEW
          </span>
          <h1 className="rv-serif-heading rv-db-main-title">
            Store Sales &amp; Overview
          </h1>
        </div>

        <div className="rv-db-header-actions">
          <div className="rv-period-status-pill">
            <span className="rv-pulse-dot" />
            <span>Period: {selectedPeriod}</span>
          </div>

          <button
            className="rv-apply-btn rv-refresh-btn"
            style={{ borderRadius: "20px", display: "inline-flex", alignItems: "center", gap: "6px" }}
            onClick={handleRefresh}
            title="Refresh store data"
          >
            <span
              style={{
                display: "inline-block",
                transform: isRefreshing ? "rotate(360deg)" : "none",
                transition: "transform 0.6s ease",
              }}
            >
              ↺
            </span>{" "}
            Refresh Data
          </button>
        </div>
      </div>

      {/* ── 2. Date Filter Bar ── */}
      <div className="rv-filter-bar">
        <div className="rv-filter-left">
          <span className="rv-filter-label">PERIOD:</span>
          <div className="rv-filter-pills">
            {periods.map((p) => (
              <button
                key={p}
                className={`rv-filter-pill-btn ${selectedPeriod === p ? "active" : ""}`}
                onClick={() => handlePeriodChange(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="rv-filter-right">
          <div className="rv-filter-date-row">
            <div className="rv-date-input-wrap">
              <span className="rv-date-input-label">FROM</span>
              <input
                type="date"
                className="rv-date-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <span className="rv-date-sep">—</span>
            <div className="rv-date-input-wrap">
              <span className="rv-date-input-label">TO</span>
              <input
                type="date"
                className="rv-date-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>
          <button className="rv-apply-btn rv-apply-filter-btn" onClick={handleApplyDates}>
            Apply
          </button>
        </div>
      </div>

      {/* ── 3. Four KPI Cards ── */}
      <div className="rv-kpi-grid">
        {/* Card 1 — Total Sales (Compact) */}
        <div className="rv-kpi-card rv-card" style={{ padding: "18px 20px" }}>
          <div>
            <div className="rv-kpi-top" style={{ marginBottom: 6 }}>
              <span className="rv-badge-label" style={{ color: "#8A7A6B" }}>
                TOTAL SALES
              </span>
            </div>
            <div className="rv-serif-heading rv-kpi-value" style={{ margin: "6px 0 0", fontSize: "28px", color: "#2C2C2C", fontWeight: 700 }}>
              {summaryLoading ? (
                <LoadingSpinner size={20} text="" />
              ) : summaryError ? (
                <span style={{ fontSize: "16px", color: "#8A7A6B" }}>Unavailable</span>
              ) : (
                `₹${(summaryData?.totalRevenue ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              )}
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            {revenueTrendPoints.length >= 2 ? (
              (() => {
                const maxAmt = Math.max(...revenueTrendPoints.map(p => p.amount), 1);
                const minAmt = Math.min(...revenueTrendPoints.map(p => p.amount));
                const range = maxAmt - minAmt || 1;
                const width = 180;
                const height = 30;
                const points = revenueTrendPoints.map((p, idx) => {
                  const x = 6 + (idx / (revenueTrendPoints.length - 1)) * (width - 12);
                  const y = height - 5 - ((p.amount - minAmt) / range) * (height - 10);
                  return { x, y };
                });
                const dPath = points.reduce((acc, pt, idx) => `${acc} ${idx === 0 ? "M" : "L"} ${pt.x.toFixed(1)} ${pt.y.toFixed(1)}`, "");
                const areaPath = `${dPath} L ${points[points.length - 1].x.toFixed(1)} ${height} L ${points[0].x.toFixed(1)} ${height} Z`;
                const lastPt = points[points.length - 1];

                return (
                  <svg width="100%" height="30" viewBox="0 0 180 30" fill="none">
                    <defs>
                      <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#A07850" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#A07850" stopOpacity="0.0" />
                      </linearGradient>
                    </defs>
                    <path d={areaPath} fill="url(#sparkGrad)" />
                    <path d={dPath} stroke="#A07850" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    <circle cx={lastPt.x} cy={lastPt.y} r="3" fill="#A07850" />
                  </svg>
                );
              })()
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 26, fontSize: 11, color: "#8A7A6B" }}>
                <></>

              </div>
            )}
          </div>
        </div>


        {/* Card 2 — Category Split Summary (Modular Subparts) */}
        <div className="rv-kpi-card rv-card" style={{ padding: "18px 20px" }}>
          <div>
            <div className="rv-kpi-top" style={{ marginBottom: 4 }}>
              <span className="rv-badge-label" style={{ color: "#8A7A6B" }}>
                CATEGORY SPLIT
              </span>
              <span style={{ fontSize: "11px", color: "#8A7A6B", fontWeight: 500 }}>{selectedPeriod}</span>
            </div>
            <div>
              {revCatLoading ? (
                <LoadingSpinner size={20} text="" />
              ) : (
                <div className="rv-kpi-split-subparts">
                  {coreCategorySplits.map((cat) => (
                    <div
                      key={cat.name}
                      className={`rv-kpi-subpart-item ${cat.hasData ? "rv-kpi-subpart-item--active" : ""}`}
                    >
                      <div className="rv-kpi-subpart-header">
                        <span className="rv-kpi-subpart-title">
                          <span
                            className="rv-color-dot"
                            style={{
                              background: cat.color,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              display: "inline-block",
                            }}
                          />
                          {cat.name}
                        </span>
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            color: cat.hasData ? "#A07850" : "#8A7A6B",
                            background: cat.hasData ? "rgba(160, 120, 80, 0.1)" : "transparent",
                            padding: cat.hasData ? "1px 6px" : "0",
                            borderRadius: "6px",
                          }}
                        >
                          {cat.hasData ? `${cat.sharePct}%` : "—"}
                        </span>
                      </div>
                      <div className="rv-kpi-subpart-value">
                        {cat.hasData
                          ? `₹${cat.revenue.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : "—"}
                      </div>
                      {/* <div className="rv-kpi-subpart-sub">
                        <span>{cat.hasData ? "Active ledger" : "No sales"}</span>
                      </div> */}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Proportional Segmented Bar */}
          <div style={{ marginTop: "4px" }}>
            {(() => {
              const activePillars = coreCategorySplits.filter((p) => p.hasData && p.revenue > 0);
              const totalActiveRev = activePillars.reduce((sum, p) => sum + p.revenue, 0);

              if (totalActiveRev > 0) {
                let currentX = 0;
                return (
                  <svg width="100%" height="7" viewBox="0 0 200 7" style={{ display: "block" }}>
                    {activePillars.map((cat) => {
                      const sharePct = (cat.revenue / totalActiveRev) * 100;
                      const width = Math.max(8, (sharePct / 100) * 196);
                      const x = currentX;
                      currentX += width + 2;
                      return (
                        <rect
                          key={cat.name}
                          x={x}
                          y="0"
                          width={width}
                          height="7"
                          rx="3"
                          fill={cat.color}
                        />
                      );
                    })}
                  </svg>
                );
              }
              return (
                <div style={{ height: "7px", background: "#EFE9E0", borderRadius: "3px" }} />
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Middle Split Row: Monthly Revenue Trend (Left) & Category Sales & Stock (Right) ── */}
      <div className="rv-middle-split-grid">
        {/* Left Card: Monthly Revenue Trend (Matched to Reference Design) */}
        <div className="rv-card rv-chart-card" style={{ padding: "30px 34px", borderRadius: 18, background: "#FFFFFF", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          {/* Card Title Row */}
          <div className="rv-card-title-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 0 }}>
            <div>
              <h2 className="rv-serif-heading rv-card-heading" style={{ fontSize: 25, fontWeight: 600, color: "#1A1A1A", margin: "0 0 5px 0", fontFamily: "'Cormorant Garamond', Georgia, serif", letterSpacing: "-0.01em" }}>
                Monthly Revenue
              </h2>
              <p className="rv-card-subheading" style={{ fontSize: 13.5, color: "#756D63", margin: 0 }}>
                Finalised revenue accrued across current fiscal half
              </p>
            </div>
            <div
              className="rv-highlight-pill"
              style={{
                background: "#FCF8F3",
                border: "1px solid #E8DCCB",
                borderRadius: "20px",
                padding: "6px 16px",
                fontSize: "13px",
                fontWeight: 600,
                color: "#7A532B",
                whiteSpace: "nowrap",
              }}
            >
              {monthlySalesData.bestMonth && monthlySalesData.bestMonth.revenue > 0
                ? `★ Best Month: ${monthlySalesData.bestMonth.month.replace(/\s\d{2}$/, "")} ${monthlySalesData.bestMonth.label}`
                : "★ Last 6 Months"}
            </div>
          </div>

          {/* Top Divider Line */}
          <div style={{ height: "1px", background: "#F1EAE1", margin: "20px 0 26px" }} />

          {/* Flexbox Bar Chart - Full Width, No Unused Side Margins */}
          <div style={{ width: "100%", flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
            {/* Bars Row spanning full card width */}
            <div style={{ display: "flex", alignItems: "flex-end", width: "100%", height: 210 }}>
              {monthlySalesData.months.map((item) => {
                const maxRev = Math.max(monthlySalesData.maxRevenue, 1000);
                const maxBarHeight = 175;
                const barHeight = item.revenue > 0
                  ? Math.max(16, Math.round((item.revenue / maxRev) * maxBarHeight))
                  : 8;
                const isHighlight = item.isBest && item.revenue > 0;

                // Color styling:
                // Best month: deep golden bronze (#8D653E) with drop shadow
                // Active / Moderate: warm taupe (#BFA586)
                // Inactive / Low: soft champagne (#E5DCBE)
                let barColor = "#E5DCBE";
                if (isHighlight) {
                  barColor = "#8D653E";
                } else if (item.revenue > 0 && item.revenue >= maxRev * 0.4) {
                  barColor = "#BFA586";
                } else if (item.revenue === 0) {
                  barColor = "#EDE6DC";
                }

                return (
                  <div
                    key={item.month}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      height: "100%",
                      padding: "0 6px",
                      boxSizing: "border-box",
                    }}
                  >
                    {/* Amount label above best month only */}
                    {isHighlight ? (
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#7A5126",
                          marginBottom: 8,
                          whiteSpace: "nowrap",
                          lineHeight: 1,
                          textAlign: "center",
                          fontFamily: "'Cormorant Garamond', Georgia, serif",
                        }}
                      >
                        {item.label}
                      </div>
                    ) : (
                      <div style={{ height: "22px", marginBottom: 8 }} />
                    )}

                    {/* Bar Pill - Centers within its flex-1 column */}
                    <div
                      title={`${item.month}: ${item.label}`}
                      style={{
                        width: "100%",
                        maxWidth: "56px",
                        height: `${barHeight}px`,
                        background: barColor,
                        borderRadius: "8px 8px 0 0",
                        boxShadow: isHighlight ? "0 6px 16px rgba(141, 101, 62, 0.28)" : "none",
                        transition: "all 0.25s ease",
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Baseline Horizontal Divider directly under the bars spanning full width */}
            <div style={{ height: "1px", background: "#ECE3D8", width: "100%", margin: 0 }} />

            {/* Month Labels under baseline spanning full width */}
            <div style={{ display: "flex", width: "100%", marginTop: 12 }}>
              {monthlySalesData.months.map((item) => {
                const isHighlight = item.isBest && item.revenue > 0;
                return (
                  <div
                    key={item.month}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      fontSize: isHighlight ? "13.5px" : "13px",
                      fontWeight: isHighlight ? 700 : 500,
                      color: isHighlight ? "#1A1A1A" : "#756D63",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    }}
                  >
                    {item.month}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Divider Line */}
          <div style={{ height: "1px", background: "#F1EAE1", margin: "24px 0 16px" }} />

          {/* Meta Footer */}
          <div
            className="rv-chart-meta-footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "13px",
              flexWrap: "wrap",
              gap: 6,
              borderTop: "none",
              paddingTop: 0,
              marginTop: 0,
            }}
          >

          </div>
        </div>

        {/* Right Card: Unified Category Portfolios & Stock Distribution (Expanded) */}
        <div className="rv-portfolios-card rv-card" style={{ padding: "28px 30px", borderRadius: 16 }}>
          <div className="rv-card-title-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>

              <h2 className="rv-serif-heading rv-card-heading" style={{ fontSize: 19, margin: "3px 0 2px" }}>
                Sales &amp; Stock by Category
              </h2>
              <p className="rv-card-subheading" style={{ fontSize: 11.5, color: "#8A7A6B", margin: 0 }}>
                Sales and available stock across all jewellery categories
              </p>
            </div>
            <div
              className="rv-highlight-pill"
              style={{
                background: "#FAF5EE",
                border: "1px solid rgba(160, 120, 80, 0.22)",
                borderRadius: "16px",
                padding: "3px 10px",
                fontSize: "11px",
                fontWeight: 600,
                color: "#A07850",
                whiteSpace: "nowrap",
              }}
            >
              ★ Live Stock &amp; Sales
            </div>
          </div>

          <div style={{ marginTop: 4, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {revCatLoading && stockWeightLoading ? (
              <LoadingSpinner size={24} text="Loading category distribution..." />
            ) : revCatError && stockWeightError ? (
              <p style={{ fontSize: "12px", color: "#8A7A6B", fontStyle: "italic", margin: "20px 0" }}>
                Unable to load category sales and stock data.
              </p>
            ) : combinedCategoryData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "28px 12px", color: "#8A7A6B", fontSize: "12px", fontStyle: "italic" }}>
                No sales or stock recorded for this period.
              </div>
            ) : (
              <div className="rv-portfolios-grid">
                {categories.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", padding: 0 }}>
                    <svg width="185" height="185" viewBox="0 0 210 210" style={{ display: "block" }}>
                      {/* Background soft track ring */}
                      <circle
                        cx="105"
                        cy="105"
                        r="82"
                        fill="none"
                        stroke="#F1EAE1"
                        strokeWidth="24"
                      />

                      {/* Slices */}
                      <g transform="rotate(-90 105 105)">
                        {categories.length === 1 && (categories[0].pct >= 99 || categories[0].pct === 0) ? (
                          <circle
                            cx="105"
                            cy="105"
                            r="82"
                            fill="none"
                            stroke={categories[0].color}
                            strokeWidth="24"
                          />
                        ) : (
                          donutSlices.map((cat) => (
                            <circle
                              key={cat.name}
                              cx="105"
                              cy="105"
                              r="82"
                              fill="none"
                              stroke={cat.color}
                              strokeWidth="24"
                              strokeDasharray={`${cat.len} ${cat.circ}`}
                              strokeDashoffset={-cat.offset}
                              strokeLinecap="butt"
                            />
                          ))
                        )}
                      </g>

                      {/* Center Text */}
                      <text
                        x="105"
                        y="90"
                        textAnchor="middle"
                        fontSize="10"
                        letterSpacing="0.14em"
                        fontWeight="700"
                        fill="#8A7A6B"
                      >
                        SALES
                      </text>
                      <text
                        x="105"
                        y="115"
                        textAnchor="middle"
                        fontSize="22"
                        fontWeight="700"
                        fontFamily="'Cormorant Garamond', Georgia, serif"
                        fill="#2C2C2C"
                      >
                        {categories.length === 1 ? "100%" : "Split"}
                      </text>
                      <text
                        x="105"
                        y="134"
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="500"
                        fill="#A07850"
                      >
                        {categories.length} {categories.length === 1 ? "Category" : "Categories"}
                      </text>
                    </svg>
                  </div>
                )}

                {/* Combined Table */}
                <div style={{ width: "100%", margin: 0, padding: 0, overflowX: "auto" }}>
                  <table className="rv-table rv-custom-table" style={{ margin: 0, width: "100%", fontSize: "11.5px" }}>
                    <thead>
                      <tr>
                        <th style={{ padding: "8px 6px", width: "32%" }}>CATEGORY</th>
                        <th style={{ textAlign: "right", padding: "8px 6px", width: "30%" }}>SALES &amp; SHARE</th>
                        <th style={{ textAlign: "right", padding: "8px 6px", width: "19%" }}>STOCK</th>
                        <th style={{ textAlign: "right", padding: "8px 6px", width: "19%" }}>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinedCategoryData.map((row) => {
                        const status = getStockStatusBadge(row.pieces);
                        return (
                          <tr key={row.name}>
                            <td style={{ padding: "8px 6px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <span className="rv-color-dot" style={{ background: row.color, width: 8, height: 8, borderRadius: "50%", flexShrink: 0 }} />
                                <span style={{ fontWeight: 600, color: "#2C2C2C", fontSize: "11.5px", whiteSpace: "nowrap" }}>{row.name}</span>
                              </div>
                            </td>
                            <td style={{ textAlign: "right", padding: "8px 6px", whiteSpace: "nowrap" }}>
                              <span style={{ fontWeight: 600, color: "#2C2C2C" }}>
                                ₹{row.revenue.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </span>
                              <span
                                style={{
                                  display: "inline-block",
                                  marginLeft: 5,
                                  background: "rgba(160, 120, 80, 0.1)",
                                  color: "#A07850",
                                  padding: "1px 5px",
                                  borderRadius: "6px",
                                  fontSize: "10px",
                                  fontWeight: 600,
                                }}
                              >
                                {row.revenuePct}%
                              </span>
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 600, padding: "8px 6px", color: "#2C2C2C", fontSize: "11.5px", whiteSpace: "nowrap" }}>
                              {row.pieces !== null && row.pieces !== undefined && !isNaN(row.pieces)
                                ? `${row.pieces} pcs`
                                : "—"}
                            </td>
                            <td style={{ textAlign: "right", padding: "8px 6px" }}>
                              {status ? (
                                <span
                                  style={{
                                    display: "inline-block",
                                    background: status.background,
                                    color: status.color,
                                    padding: "2px 7px",
                                    borderRadius: "8px",
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {status.label}
                                </span>
                              ) : (
                                <span style={{ color: "#8A7A6B" }}>—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div
            className="rv-chart-meta-footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid rgba(160, 120, 80, 0.1)",
              paddingTop: 10,
              marginTop: 6,
              fontSize: "11px",
              flexWrap: "wrap",
              gap: 6,
            }}
          >
            {/* <span style={{ color: "#27AE60", fontWeight: 600 }}>
              Active Categories: {combinedCategoryData.length}
            </span>
            <span style={{ fontStyle: "italic", color: "#8A7A6B" }}>
              Live vault &amp; sales sync
            </span> */}
          </div>
        </div>
      </div>

      {/* ── 5. Two Column Cards (Best Sellers & Quick Actions) ── */}
      <div className="rv-two-col-grid">
        {/* Column 1 — Top 5 Products */}
        <div className="rv-col-card rv-card">
          <div>
            <div style={{ marginBottom: 14 }}>
              <span className="rv-badge-label">BEST SELLERS</span>
              <h2 className="rv-serif-heading rv-card-heading" style={{ marginTop: 3 }}>
                Top 5 Products
              </h2>
            </div>

            {topProductsLoading ? (
              <LoadingSpinner size={22} text="Loading top products..." />
            ) : topProductsError ? (
              <p style={{ fontSize: "12px", color: "#8A7A6B", fontStyle: "italic", margin: "16px 0" }}>
                Unable to load product rankings.
              </p>
            ) : topProducts.length === 0 ? (
              <p style={{ fontSize: "12px", color: "#8A7A6B", fontStyle: "italic", margin: "20px 0" }}>
                No product sales recorded for this period.
              </p>
            ) : (
              <div className="rv-top-products-list">
                {topProducts.slice(0, 5).map((p, idx) => (
                  <div key={p.productID || idx} className="rv-product-row">
                    <div className="rv-product-info">
                      <span className="rv-product-rank">{idx + 1}.</span>
                      <div>
                        <div className="rv-product-name">{p.productName || "Product"}</div>
                        <div className="rv-product-sold">
                          {p.unitsSold ?? 0} units sold • ₹{(p.revenue || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid rgba(160, 120, 80, 0.1)", paddingTop: 12 }}>
            <span
              className="rv-card-action-link"
              onClick={() => navigateTo("Products")}
            >
              View all products →
            </span>
          </div>
        </div>

        {/* Column 2 — Quick Actions */}
        <div className="rv-col-card rv-card">
          <div>
            <div style={{ marginBottom: 14 }}>
              <span
                className="rv-badge-label"
                style={{ color: "#A07850" }}
              >
                QUICK ACTIONS
              </span>
              <p className="rv-card-subheading" style={{ marginTop: 2, marginBottom: 0, fontSize: "12px", color: "#8A7A6B" }}>
                Common store tasks
              </p>
            </div>

            <div className="rv-quick-actions-grid">
              <button
                type="button"
                className="rv-quick-action-card"
                onClick={() => handleShortPath("Invoice", "new-invoice")}
              >
                <div className="rv-quick-action-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <polyline points="10 9 9 9 8 9" />
                  </svg>
                </div>
                <span className="rv-quick-action-name">New Invoice</span>
              </button>

              <button
                type="button"
                className="rv-quick-action-card"
                onClick={() => handleShortPath("Stock Receipt", "new-receipt")}
              >
                <div className="rv-quick-action-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </div>
                <span className="rv-quick-action-name">Receive Stock</span>
              </button>

              <button
                type="button"
                className="rv-quick-action-card"
                onClick={() => handleShortPath("Products", "add-product")}
              >
                <div className="rv-quick-action-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                </div>
                <span className="rv-quick-action-name">Add Product</span>
              </button>

              <button
                type="button"
                className="rv-quick-action-card"
                onClick={() => handleShortPath("Customer Notes", "new-note")}
              >
                <div className="rv-quick-action-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                    <path d="M10 9H8" />
                  </svg>
                </div>
                <span className="rv-quick-action-name">Customer Notes</span>
              </button>
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(160, 120, 80, 0.1)", paddingTop: 12 }}>
            <span
              className="rv-card-action-link"
              onClick={() => navigateTo("Customer Notes")}
            >
              View customer notes →
            </span>
          </div>
        </div>
      </div>

      {/* ── 6. Recent Invoices Table ── */}
      <div className="rv-table-card rv-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
          <div>
            <span className="rv-badge-label">RECENT BILLS</span>
            <h2 className="rv-serif-heading rv-card-heading" style={{ marginTop: 3 }}>
              Recent Bills &amp; Invoices
            </h2>
            <p className="rv-card-subheading">
              Latest customer bills and saved estimates
            </p>
          </div>
          <div>
            <span
              className="rv-card-action-link"
              onClick={() => navigateTo("Invoice")}
            >
              View all customer bills →
            </span>
          </div>
        </div>

        {invoicesLoading ? (
          <LoadingSpinner size={24} text="Loading recent bills..." />
        ) : invoicesError ? (
          <p style={{ fontSize: "12px", color: "#8A7A6B", fontStyle: "italic", padding: "16px 0" }}>
            Unable to load recent bills at this time.
          </p>
        ) : recentInvoices.length === 0 ? (
          <p style={{ fontSize: "12px", color: "#8A7A6B", fontStyle: "italic", padding: "16px 0" }}>
            No recent bills recorded yet. Click "New Invoice" below to create one.
          </p>
        ) : (
          <>
            <div className="rv-table-desktop">
              <div className="rv-table-wrap">
                <table className="rv-table rv-custom-table">
                  <thead>
                    <tr>
                      <th>BILL NO.</th>
                      <th>CUSTOMER NAME</th>
                      <th>ITEMS PURCHASED</th>
                      <th>AMOUNT</th>
                      <th>STATUS</th>
                      <th style={{ textAlign: "right" }}>ACTION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentInvoices.map((inv) => {
                      const status = inv.invoiceStatus || "Draft";
                      const isFinalised = status.toLowerCase().startsWith("final");
                      return (
                        <tr key={inv.invoiceID || inv.invoiceNumber}>
                          <td className="rv-invoice-no">
                            {inv.invoiceNumber || `INV-${inv.invoiceID}`}
                          </td>
                          <td>
                            <div className="rv-customer-name">
                              {inv.customerName || "Walk-in Customer"}
                            </div>
                            <div className="rv-customer-phone">
                              {inv.customerPhone || "—"}
                            </div>
                          </td>
                          <td>
                            <div className="rv-items-desc" title={inv._itemDescription || inv.itemsDescription || inv.invoiceDate}>
                              {DEMO_MODE && inv._itemDescription
                                ? inv._itemDescription
                                : (inv.itemsDescription || `Invoice dated ${inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}`)}
                            </div>
                          </td>
                          <td className="rv-amount-val">
                            ₹{(inv.totalAmount || inv.grossAmount || 0).toLocaleString("en-IN")}
                          </td>
                          <td>
                            <span
                              className={`rv-pill-badge ${isFinalised ? "rv-pill-badge--finalised" : "rv-pill-badge--draft"
                                }`}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: isFinalised ? "#27AE60" : "#D97706",
                                }}
                              />
                              {status}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button
                              className="rv-table-action-btn"
                              onClick={() => {
                                if (isFinalised) {
                                  setPrintInvoice(inv);
                                } else {
                                  sessionStorage.setItem("reva_review_invoice_id", String(inv.invoiceID));
                                  navigateTo("Invoice");
                                }
                              }}
                            >
                              {isFinalised ? "Print" : "Review"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rv-invoices-mobile-list">
              {recentInvoices.map((inv) => {
                const status = inv.invoiceStatus || "Draft";
                const isFinalised = status.toLowerCase().startsWith("final");
                return (
                  <div key={inv.invoiceID || inv.invoiceNumber} className="rv-inv-mob-card">
                    <div className="rv-inv-mob-header">
                      <span className="rv-inv-mob-no">
                        {inv.invoiceNumber || `INV-${inv.invoiceID}`}
                      </span>
                      <span
                        className={`rv-pill-badge ${isFinalised ? "rv-pill-badge--finalised" : "rv-pill-badge--draft"
                          }`}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: isFinalised ? "#27AE60" : "#D97706",
                          }}
                        />
                        {status}
                      </span>
                    </div>
                    <div className="rv-inv-mob-body">
                      <div className="rv-inv-mob-customer">
                        {inv.customerName || "Walk-in Customer"}
                      </div>
                      {inv.customerPhone && inv.customerPhone !== "—" && (
                        <div className="rv-inv-mob-phone">{inv.customerPhone}</div>
                      )}
                      <div className="rv-inv-mob-desc">
                        {inv.itemsDescription || `Invoice dated ${inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}`}
                      </div>
                    </div>
                    <div className="rv-inv-mob-footer">
                      <div className="rv-inv-mob-amount">
                        ₹{(inv.totalAmount || inv.grossAmount || 0).toLocaleString("en-IN")}
                      </div>
                      <button
                        className="rv-table-action-btn"
                        onClick={() => {
                          if (isFinalised) {
                            setPrintInvoice(inv);
                          } else {
                            sessionStorage.setItem("reva_review_invoice_id", String(inv.invoiceID));
                            navigateTo("Invoice");
                          }
                        }}
                      >
                        {isFinalised ? "Print" : "Review"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── 7. Print Invoice Modal (1-click direct print from dashboard) ── */}
      {printInvoice && (
        <PrintInvoiceModal
          invoice={printInvoice}
          lines={(invoiceItemsData || []).filter(i => Number(i.invoiceID) === Number(printInvoice.invoiceID))}
          products={productsData || []}
          categories={categoriesData || []}
          inventoryItems={inventoryItemsData || []}
          onClose={() => setPrintInvoice(null)}
        />
      )}

      {/* ── Footer ── */}
      <div className="rv-dashboard-footer">
        <div>
          © 2026 Revaa Fine Jewellers • Store Management
        </div>
        <div>
          Certified Jewellery • Hallmark &amp; Quality Assured
        </div>
      </div>
    </div>
  );
}
